import React, { useEffect, useState } from 'react';
import { Download, Smartphone, Share2, X } from 'lucide-react';
import { toast } from 'sonner';

const DISMISS_KEY = 'jd_install_banner_dismissed_at';
// 7 days — if the user X'd the banner we won't pester them again for a week
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SHARE_URL = 'https://justdeliver.ie/install';
const SHARE_TEXT =
  "Check out JustDeliver — post a delivery, drivers bid, you choose the best price. Install the app:";

/**
 * InstallAppBanner — full-width premium install prompt rendered just under
 * the landing-page header. Matches the JustDeliver brand (white-green bg,
 * green install button with orange download arrow, dismissable X).
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │  [📱]  Install JustDeliver App           [Install ⇩]    [×]    │
 *   │        for faster bidding & updates                            │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Visibility rules:
 *   - Hides if the app is already installed / running standalone
 *   - Hides if the user dismissed it within DISMISS_TTL_MS
 *   - On Android Chrome: shows once `beforeinstallprompt` fires, clicking
 *     the Install button triggers the native install prompt
 *   - On iOS Safari: shows immediately with a "Share → Add to Home Screen"
 *     hint (iOS has no programmatic install API)
 *   - Otherwise: hidden (don't render on desktop browsers that will never
 *     fire the prompt)
 */
export default function InstallAppPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    // Hidden if already installed
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }
    // Hidden if user dismissed within the TTL window
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_TTL_MS) {
        setDismissed(true);
      }
    } catch {
      /* localStorage blocked — show banner */
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;

  const ua = typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;

  // Hide on desktop browsers that will never fire beforeinstallprompt
  if (!deferredPrompt && !isIos) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') setInstalled(true);
      } catch {
        /* user dismissed native prompt */
      }
      setDeferredPrompt(null);
      return;
    }
    if (isIos) setShowIosTip((v) => !v);
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* localStorage blocked — banner stays hidden for the session only */
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    // Native share sheet (mobile) — gives the user WhatsApp, Messages, etc.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: 'JustDeliver — Fast, Local Delivery',
          text: SHARE_TEXT,
          url: SHARE_URL,
        });
        return;
      } catch {
        // User cancelled, or share API failed — fall through to copy
      }
    }
    // Desktop fallback — copy the link
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      toast.success('Download link copied — share it anywhere!');
    } catch {
      // Final fallback: open mailto with the message
      window.open(
        `mailto:?subject=${encodeURIComponent('Try JustDeliver')}&body=${encodeURIComponent(`${SHARE_TEXT}\n${SHARE_URL}`)}`,
        '_blank'
      );
    }
  };

  return (
    <div
      className="install-banner-enter w-full bg-emerald-50/70 border-y border-emerald-100"
      data-testid="install-app-banner"
      role="region"
      aria-label="Install JustDeliver app"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-12 py-2.5 sm:py-3">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Phone icon tile */}
          <div className="flex-shrink-0 relative w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white border border-emerald-100 shadow-sm flex items-center justify-center">
            <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-[#1B5E20]" strokeWidth={1.75} />
            <Download
              className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-[18px] sm:h-[18px] text-[#F57E0E] bg-white rounded-full p-0.5 ring-1 ring-emerald-100"
              strokeWidth={2.5}
            />
          </div>

          {/* Copy */}
          <div className="flex-1 min-w-0">
            <p
              className="text-sm sm:text-base font-bold text-[#050505] leading-tight truncate"
              data-testid="install-banner-title"
            >
              Install <span className="text-[#2E7D32]">JustDeliver</span> App
            </p>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5 truncate">
              for faster bidding &amp; updates
            </p>
            {showIosTip && isIos && (
              <p
                className="mt-1 text-[11px] text-[#4B5563] leading-snug"
                data-testid="install-banner-ios-tip"
              >
                On iPhone: tap <span className="font-semibold">Share</span> →
                <span className="font-semibold"> Add to Home Screen</span>.
              </p>
            )}
          </div>

          {/* Share — native share sheet on mobile, copy on desktop */}
          <button
            type="button"
            onClick={handleShare}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg bg-white hover:bg-emerald-50 text-[#1B5E20] text-sm font-bold border border-emerald-200 transition-all active:scale-95"
            data-testid="install-banner-share"
            aria-label="Share JustDeliver app download link"
            title="Share download link"
          >
            <Share2 className="w-4 h-4" strokeWidth={2.25} />
            <span className="hidden sm:inline">Share</span>
          </button>

          {/* Install button */}
          <button
            type="button"
            onClick={handleInstall}
            className="flex-shrink-0 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-bold shadow-sm transition-all active:scale-95"
            data-testid="install-banner-cta"
            aria-label="Install JustDeliver app"
          >
            <Download className="w-4 h-4 text-[#F57E0E]" strokeWidth={2.5} />
            <span>Install</span>
          </button>

          {/* Dismiss */}
          <button
            type="button"
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 -mr-1 sm:mr-0 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            data-testid="install-banner-dismiss"
            aria-label="Dismiss install banner"
          >
            <X className="w-4 h-4 sm:w-[18px] sm:h-[18px]" strokeWidth={2.25} />
          </button>
        </div>
      </div>
    </div>
  );
}
